import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { setAuth } from '../utils/auth';
import { toast } from 'sonner';
import PublicHeader from '../components/PublicHeader';
import { useTranslation } from '../hooks/useTranslation';

const DEMO_ACCOUNTS = [
  { key: 'patient', label: 'Patient', emoji: '🧑', email: 'demo.patient@medrahi.com' },
  { key: 'doctor', label: 'Doctor', emoji: '👨‍⚕️', email: 'demo.doctor@medrahi.com' },
  { key: 'hospital', label: 'Hospital', emoji: '🏥', email: 'demo.hospital@medrahi.com' },
  { key: 'case-manager', label: 'Case Manager', emoji: '🗂️', email: 'demo.case_manager@medrahi.com' },
];

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [processingOAuth, setProcessingOAuth] = useState(false);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [demoLoading, setDemoLoading] = useState('');
  const [needsVerification, setNeedsVerification] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    let active = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/demo-mode`)
      .then((r) => r.json())
      .then((d) => { if (active) setDemoEnabled(!!d.enabled); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const redirectByRole = (role) => {
    const adminRoles = ['super_admin', 'admin', 'doctor_verifier', 'finance_admin', 'moderator', 'support_admin'];
    if (role === 'patient') navigate('/patient/dashboard');
    else if (role === 'doctor') navigate('/doctor/dashboard');
    else if (role === 'hospital') navigate('/hospital/dashboard');
    else if (role === 'case_manager') navigate('/case_manager/dashboard');
    else if (adminRoles.includes(role)) navigate('/admin/dashboard');
    else navigate('/patient/dashboard');
  };

  const doDemoLogin = async (acct) => {
    setDemoLoading(acct.email);
    try {
      const { data } = await api.post('/auth/demo-login', { account: acct.key });
      setAuth(data.user);
      toast.success(`Logged in as ${acct.label}`);
      redirectByRole(data.user.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Demo login failed');
    } finally {
      setDemoLoading('');
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Handle Google OAuth callback - process session_id from URL fragment
  useEffect(() => {
    const processOAuthCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        setProcessingOAuth(true);
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        
        if (sessionId) {
          try {
            // Clear the hash from URL
            window.history.replaceState(null, '', window.location.pathname);
            
            // Exchange session_id for user data
            const { data } = await api.post('/auth/google/session', { session_id: sessionId });
            
            setAuth(data.user);
            toast.success(`Welcome, ${data.user.full_name}!`);
            
            // Redirect based on role
            const role = data.user.role;
            if (role === 'patient') navigate('/patient/dashboard');
            else if (role === 'doctor') navigate('/doctor/dashboard');
            else if (role === 'hospital') navigate('/hospital/dashboard');
            else if (role === 'case_manager') navigate('/case_manager/dashboard');
            else if (role === 'super_admin' || role === 'admin') navigate('/admin/dashboard');
            else navigate('/patient/dashboard');
          } catch (error) {
            toast.error('Google sign-in failed. Please try again.');
            setProcessingOAuth(false);
          }
        }
      }
    };
    
    processOAuthCallback();
  }, [navigate]);

  // Handle Google Sign-In button click
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await api.post('/auth/login', { email, password });
      setAuth(data.user);
      toast.success('Login successful!');
      
      // Redirect based on role
      const role = data.user.role;
      if (role === 'patient') navigate('/patient/dashboard');
      else if (role === 'doctor') navigate('/doctor/dashboard');
      else if (role === 'hospital') navigate('/hospital/dashboard');
      else if (role === 'case_manager') navigate('/case_manager/dashboard');
      else if (role === 'super_admin' || role === 'admin') navigate('/admin/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (typeof detail === 'string' && detail.includes('EMAIL_NOT_VERIFIED')) {
        setNeedsVerification(email);
        toast.error('Please verify your email before signing in.');
      } else {
        toast.error(detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-verification', { email: needsVerification || email });
      toast.success('Verification email sent. Please check your inbox.');
    } catch (e) {
      toast.error('Could not resend verification email.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(193_95%_95%)] to-[hsl(210_40%_98%)]">
      <PublicHeader currentPage="login" />
      <div className="pt-32 pb-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-[hsl(var(--foreground))]" data-testid="login-title">
              {t('login.title')}
            </h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
              {t('login.subtitle')}
            </p>
          </div>

          {needsVerification && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="verification-notice">
              <p className="text-amber-800 font-medium">Your email isn't verified yet.</p>
              <p className="text-amber-700 mt-1">Check your inbox for the verification link, or resend it below.</p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resending}
                className="mt-3 text-[hsl(var(--primary))] font-semibold hover:underline disabled:opacity-50"
                data-testid="resend-verification-btn"
              >
                {resending ? 'Sending…' : 'Resend verification email'}
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="email">
                {t('login.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" 
                data-testid="login-email-input"
                placeholder={t('login.emailPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="password">
                {t('login.passwordLabel')}
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                data-testid="login-password-input"
                placeholder={t('login.passwordPlaceholder')}
              />
            </div>

            <button
              type="submit"
              disabled={loading || processingOAuth}
              className="w-full bg-[hsl(var(--primary))] text-white py-2.5 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-colors"
              data-testid="login-submit-button"
            >
              {loading ? t('login.signingIn') : t('login.signInButton')}
            </button>

            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-[hsl(var(--primary))] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </form>

          {/* Divider */}
          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-[hsl(var(--border))]"></div>
            <span className="px-4 text-sm text-[hsl(var(--muted-foreground))]">or</span>
            <div className="flex-1 border-t border-[hsl(var(--border))]"></div>
          </div>

          {/* Google Sign-In Button */}
          {processingOAuth ? (
            <div className="w-full flex items-center justify-center py-3 border border-[hsl(var(--border))] rounded-lg bg-gray-50">
              <svg className="animate-spin h-5 w-5 mr-3 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-gray-600">Processing Google Sign-In...</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-[hsl(var(--border))] rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
              data-testid="google-signin-button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Sign in with Google</span>
            </button>
          )}

          <div className="mt-6 text-center text-sm">
            <span className="text-[hsl(var(--muted-foreground))]">{t('login.noAccount')} </span>
            <Link to="/register" className="text-[hsl(var(--primary))] font-medium hover:underline" data-testid="register-link">
              {t('login.signUp')}
            </Link>
          </div>

          {demoEnabled && (
            <div className="mt-6 pt-6 border-t border-dashed border-amber-300" data-testid="demo-login-panel">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold uppercase tracking-wide text-amber-600">Demo one-click login</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">testing</span>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
                Instantly sign in as any role. Disable Demo Mode in Admin → AI Settings for production.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {DEMO_ACCOUNTS.map((acct) => (
                  <button
                    key={acct.email}
                    type="button"
                    onClick={() => doDemoLogin(acct)}
                    disabled={!!demoLoading}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-xs transition-colors disabled:opacity-50 border-slate-200 bg-slate-50 hover:bg-slate-100`}
                    data-testid={`demo-login-${acct.key}`}
                  >
                    <span className="text-base leading-none">{acct.emoji}</span>
                    <span className="flex-1">
                      <span className="block font-semibold text-slate-800">{acct.label}</span>
                      <span className="block text-[10px] text-slate-500 truncate">{acct.email}</span>
                    </span>
                    {demoLoading === acct.email && (
                      <svg className="animate-spin h-3.5 w-3.5 text-slate-500" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
